/**
 * CurrencySelector — compact dropdown that swaps the shopper's display
 * currency. Preference persists in localStorage; every price surface picks
 * up the change instantly via `useCurrency()`.
 */
import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Check, Globe } from "lucide-react";
import { useCurrency } from "@/contexts/CurrencyContext";

interface Props {
  className?: string;
  align?: "left" | "right";
  compact?: boolean;
}

export const CurrencySelector: React.FC<Props> = ({ className = "", align = "right", compact = false }) => {
  const { code, currency, currencies, setCode } = useCurrency();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div ref={wrap} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="currency-selector-btn"
        className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 text-xs font-display font-semibold hover:border-slate-900 dark:hover:border-white transition-colors"
      >
        {compact ? (
          <Globe className="h-3.5 w-3.5" strokeWidth={2.4} />
        ) : (
          <span className="text-sm leading-none" aria-hidden>{currency.flag || "\uD83C\uDF10"}</span>
        )}
        <span className="tabular-nums">{code}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={2.5} />
      </button>
      {open && (
        <div
          role="listbox"
          data-testid="currency-selector-menu"
          className={`absolute z-50 mt-2 w-56 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-[0_20px_50px_-15px_rgba(15,23,42,0.35)] overflow-hidden ${align === "right" ? "right-0" : "left-0"}`}
        >
          <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-800">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 dark:text-slate-500 font-display font-bold">
              Display currency
            </div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 leading-snug mt-0.5">
              Prices convert live. Checkout is billed in INR.
            </div>
          </div>
          <ul className="max-h-72 overflow-y-auto sg-scrollbar-thin py-1">
            {currencies.map((c) => {
              const active = c.code === code;
              return (
                <li key={c.code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => { setCode(c.code); setOpen(false); }}
                    data-testid={`currency-option-${c.code}`}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                      active
                        ? "bg-slate-50 dark:bg-slate-800/60 text-slate-900 dark:text-white"
                        : "text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/60"
                    }`}
                  >
                    <span className="text-base leading-none" aria-hidden>{c.flag || "\uD83C\uDF10"}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-semibold text-xs">{c.code}</div>
                      <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate">{c.name}</div>
                    </div>
                    <span className="text-[10px] font-mono tabular-nums text-slate-400 dark:text-slate-500">
                      {c.symbol}
                    </span>
                    {active && <Check className="h-3.5 w-3.5 text-emerald-500" strokeWidth={2.8} />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
